import React, { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import _ from 'lodash'
import Search from '../../services/Search'
import Tracker from '../../services/Tracker'
import getSortOptions, { labelForCode as sortLabelForCode } from './sortOptions'
import LotTypeSeg from './LotTypeSeg'
import { CheckIcon, ChevronIcon, CloseIcon, SortIcon } from './icons'

// DEV-1774 addendum 1 — sticky scroll bar.
// Replaces the legacy ListingFilters inside FixedHeader with the new
// 3-column inline rail design (Explore Lots | Lot Type tabs | Sort by).
//
// Self-managed state (parallel to the main FilterToolbar) following the
// existing ListingFilters pattern: inits from URL, listens to global
// `update-listing-filters` to stay in sync with the main toolbar,
// dispatches the same event on local changes.
//
// DEV-2980: now drawn as the storefront's filter bar (see the render below).

const tracker = new Tracker()

function readScrollState() {
  return {
    scrollY: typeof window === 'undefined' ? 0 : window.pageYOffset,
  }
}

export default function StickyFilterBar() {
  const searchService = useRef(null)
  if (!searchService.current) searchService.current = new Search()

  // Initial filter state from URL.
  const [filters, setFilters] = useState(() => {
    const f = searchService.current.initFilters({ props: {} })
    try {
      const qs = (typeof window !== 'undefined' && window.location && window.location.search) || ''
      const qsTrim = qs.startsWith('?') ? qs.slice(1) : qs
      searchService.current.applyQueryStrToFilters({ filters: f, queryStr: qsTrim })
    } catch (e) { /* default filters */ }
    return f
  })
  const [sortOpen, setSortOpen] = useState(false)
  const [mobileSortOpen, setMobileSortOpen] = useState(false)

  // Sync with the main toolbar via the existing global event.
  useEffect(() => {
    function onUpdate(e) {
      const incoming = e && e.detail && e.detail.filters
      if (!incoming) return
      // Replace our local filters with the broadcast ones.
      setFilters(_.cloneDeep(incoming))
    }
    document.addEventListener('update-listing-filters', onUpdate)
    return () => document.removeEventListener('update-listing-filters', onUpdate)
  }, [])

  const currentSortCode = (filters.sort_by && filters.sort_by.query && filters.sort_by.query.code) || ''
  const sortLabel = sortLabelForCode({ filters }, currentSortCode)

  function dispatchUpdate(nextFilters) {
    document.dispatchEvent(new CustomEvent('update-listing-filters', {
      detail: { filters: nextFilters }
    }))
  }

  // Sort change — commit + dispatch + tracker.
  const handleSortPick = useCallback((code) => {
    if (code === currentSortCode) { setSortOpen(false); return }
    const next = _.cloneDeep(filters)
    if (!next.sort_by) next.sort_by = { query: {} }
    if (!next.sort_by.query) next.sort_by.query = {}
    next.sort_by.query.code = code
    delete next.ab
    if (next.pagination) next.pagination.page = 1
    setFilters(next)
    try {
      const source = location.pathname.toLowerCase() === '/search' ? 'Search 2.0' : 'Home Page'
      tracker.trackSortFilterClick({ source, v1: currentSortCode, v2: code })
    } catch (e) { /* analytics failure must not block sort */ }
    dispatchUpdate(next)
    setSortOpen(false)
    setMobileSortOpen(false)
  }, [filters, currentSortCode])

  const sortOptions = getSortOptions({ filters, hide_qty_sort: true, is_relevancy_filter_enabled: false })

  // Close the desktop sort menu on an outside press or Escape (storefront fb-dd).
  const sortRef = useRef(null)
  useEffect(() => {
    if (!sortOpen) return
    function onDown(e) { if (sortRef.current && !sortRef.current.contains(e.target)) setSortOpen(false) }
    function onKey(e) { if (e.key === 'Escape') setSortOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [sortOpen])

  const typeSeg = <LotTypeSeg filters={filters} />

  // DEV-2980: drawn as the storefront's PLP filter bar (cb-storefront
  // plp/listing/Filter.tsx, FilterBar + MobileFilter), styled by
  // www/storefront_chrome.css under .sfc. Explore Lots is gone: the header's
  // Explore Our Auctions is the category menu now.
  return (
    <div className="sfc" data-component="sticky-filter-bar">
      {/* Desktop: Show: [Auction | Buy Now] .... Sort by: [label v] */}
      <div className="fb-bar sfc-desk">
        <div className="fb-bar-inner">
          <div className="fb-cluster">
            <span className="fb-label">Show:</span>
            {typeSeg}
          </div>
          <div className="fb-cluster">
            <span className="fb-label">Sort by:</span>
            <div className="fb-dd-wrap" ref={sortRef}>
              <button
                type="button"
                className="fb-dd"
                aria-expanded={sortOpen}
                aria-haspopup="menu"
                onClick={() => setSortOpen((p) => !p)}
              >
                <span>{sortLabel}</span>
                <ChevronIcon />
              </button>
              {sortOpen && (
                <div className="fb-menu fb-menu-right" role="menu">
                  {sortOptions.map(([code, label]) => (
                    <button
                      key={code}
                      type="button"
                      role="menuitemradio"
                      aria-checked={code === currentSortCode}
                      className={code === currentSortCode ? 'is-active' : ''}
                      onClick={() => handleSortPick(code)}
                    >
                      <span>{label}</span>
                      {code === currentSortCode && <CheckIcon />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Phone: full-width segmented control, then a Sort button that opens
          the storefront's sort sheet. */}
      <div className="plpr-mfilter sfc-compact">
        <div className="plpr-mfilter-seg">{typeSeg}</div>
        <div className="plpr-mfilter-actions">
          <button type="button" className="plpr-mfilter-btn" onClick={() => setMobileSortOpen(true)}>
            <SortIcon /> {sortLabel}
          </button>
        </div>
      </div>

      {/* Portaled to <body>, as the storefront does, so the backdrop covers
          the header too rather than sitting under its layer. */}
      {mobileSortOpen && createPortal(
        <div className="sfc">
          <div className="mno-backdrop" onClick={() => setMobileSortOpen(false)} aria-hidden="true" />
          <div className="mno-sheet" role="dialog" aria-label="Sort lots">
            <div className="mno-sheet-head">
              <button type="button" className="mno-close" aria-label="Close sort" onClick={() => setMobileSortOpen(false)}>
                <CloseIcon />
              </button>
              <h2 className="mno-sheet-title">Sort by</h2>
              <span aria-hidden="true" />
            </div>
            <div className="mno-sheet-body">
              <ul className="mno-sort-list">
                {sortOptions.map(([code, label]) => (
                  <li key={code}>
                    <button
                      type="button"
                      className={'mno-sort-row' + (code === currentSortCode ? ' is-active' : '')}
                      onClick={() => handleSortPick(code)}
                    >
                      <span>{label}</span>
                      {code === currentSortCode && <CheckIcon />}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
